import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import FileUploader from "@/components/FileUploader";
import MultilingualField from "@/components/MultilingualField";

const AdminSettings = () => {
  const [data, setData] = useState({
    logos: { main: "", footer: "", favicon: "", light: "", dark: "" },
    company_info: { name: "", tagline: { en: "", tr: "", id: "" }, founded_year: "" },
    social_links: { instagram: "", facebook: "", tiktok: "", linkedin: "", youtube: "", whatsapp: "", shopee: "", tokopedia: "" },
    contact: { email: "", phone: "", whatsapp: "", address: "" },
    homepage: { counters: { countries: 0, categories: 0, partners: 0, solutions: 0 }, partner_brands: [] },
    seo: { title: { en: "", tr: "", id: "" } },
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/site-settings").then((r) => setData((d) => ({
      ...d,
      ...r.data,
      logos: { ...d.logos, ...(r.data.logos || {}) },
      social_links: { ...d.social_links, ...(r.data.social_links || {}) },
      contact: { ...d.contact, ...(r.data.contact || {}) },
      company_info: { ...d.company_info, ...(r.data.company_info || {}) },
      homepage: {
        ...d.homepage,
        ...(r.data.homepage || {}),
        partner_brands: r.data.homepage?.partner_brands || [],
      },
      seo: { ...d.seo, ...(r.data.seo || {}) },
    })));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/site-settings", data);
      toast.success("Settings saved");
    } catch { toast.error("Failed"); }
    setSaving(false);
  };

  const setLogo = (k, v) => setData({ ...data, logos: { ...data.logos, [k]: v } });
  const setSocial = (k, v) => setData({ ...data, social_links: { ...data.social_links, [k]: v } });
  const setContact = (k, v) => setData({ ...data, contact: { ...data.contact, [k]: v } });
  const setCounter = (k, v) => setData({ ...data, homepage: { ...data.homepage, counters: { ...data.homepage.counters, [k]: +v } } });

  const setBrand = (idx, key, value) => {
    const brands = [...(data.homepage.partner_brands || [])];
    brands[idx] = { ...brands[idx], [key]: value };
    setData({ ...data, homepage: { ...data.homepage, partner_brands: brands } });
  };
  const addBrand = () => {
    const brands = [...(data.homepage.partner_brands || [])];
    brands.push({
      id: `brand_${Date.now()}`,
      name: "",
      tagline: { en: "", tr: "", id: "" },
      description: { en: "", tr: "", id: "" },
      image_url: "",
      link: "",
      sort_order: brands.length,
    });
    setData({ ...data, homepage: { ...data.homepage, partner_brands: brands } });
  };
  const removeBrand = (idx) => {
    if (!confirm("Remove this partner brand?")) return;
    const brands = (data.homepage.partner_brands || []).filter((_, i) => i !== idx);
    setData({ ...data, homepage: { ...data.homepage, partner_brands: brands } });
  };

  return (
    <div data-testid="admin-settings">
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-display text-3xl text-[#0A1128]">Site Settings</h1>
        <button onClick={save} disabled={saving} data-testid="save-settings" className="btn-gold disabled:opacity-50">{saving ? "..." : "Save Changes"}</button>
      </div>

      <div className="space-y-10">
        {/* Logos */}
        <Card title="Logo Management">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FileUploader label="Main Logo (Header)" value={data.logos.main} onChange={(v) => setLogo("main", v)} testId="logo-main" />
            <FileUploader label="Footer Logo" value={data.logos.footer} onChange={(v) => setLogo("footer", v)} testId="logo-footer" />
            <FileUploader label="Favicon" value={data.logos.favicon} onChange={(v) => setLogo("favicon", v)} testId="logo-favicon" />
            <FileUploader label="Light Version" value={data.logos.light} onChange={(v) => setLogo("light", v)} testId="logo-light" />
            <FileUploader label="Dark Version" value={data.logos.dark} onChange={(v) => setLogo("dark", v)} testId="logo-dark" />
          </div>
        </Card>

        {/* Company info */}
        <Card title="Company Information">
          <div className="space-y-4">
            <Input label="Company Name" v={data.company_info.name} on={(v) => setData({ ...data, company_info: { ...data.company_info, name: v } })} testId="company-name" />
            <MultilingualField label="Tagline" value={data.company_info.tagline} onChange={(v) => setData({ ...data, company_info: { ...data.company_info, tagline: v } })} testId="company-tagline" />
            <Input label="Founded Year" v={data.company_info.founded_year} on={(v) => setData({ ...data, company_info: { ...data.company_info, founded_year: v } })} testId="company-year" />
          </div>
        </Card>

        {/* Contact */}
        <Card title="Contact Information">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Email" v={data.contact.email} on={(v) => setContact("email", v)} testId="contact-email" />
            <Input label="Phone" v={data.contact.phone} on={(v) => setContact("phone", v)} testId="contact-phone" />
            <Input label="WhatsApp Number" v={data.contact.whatsapp} on={(v) => setContact("whatsapp", v)} testId="contact-whatsapp" />
            <Input label="Address" v={data.contact.address} on={(v) => setContact("address", v)} testId="contact-address" />
          </div>
        </Card>

        {/* Social */}
        <Card title="Social Media (Global)">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {["instagram", "facebook", "tiktok", "linkedin", "youtube", "whatsapp", "shopee", "tokopedia"].map((k) => (
              <Input key={k} label={k.charAt(0).toUpperCase() + k.slice(1)} v={data.social_links[k] || ""} on={(v) => setSocial(k, v)} testId={`social-${k}`} />
            ))}
          </div>
        </Card>

        {/* Counters */}
        <Card title="Homepage Counters">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {["countries", "categories", "partners", "solutions"].map((k) => (
              <div key={k}>
                <label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 block mb-2">{k}</label>
                <input type="number" value={data.homepage.counters[k] || 0} onChange={(e) => setCounter(k, e.target.value)} data-testid={`counter-${k}`} className="w-full px-4 py-3 border border-slate-300 rounded-sm text-sm" />
              </div>
            ))}
          </div>
        </Card>

        {/* Partner Brands (Sister Brands like NakiaQurbani) */}
        <Card title="Brand Family / Partner Sites">
          <p className="text-xs text-slate-500 mb-5">Showcase sister brands (like NakiaQurbani) on the homepage. Each card opens the configured external link in a new tab.</p>
          <div className="space-y-6">
            {(data.homepage.partner_brands || []).map((b, idx) => (
              <div key={b.id || idx} className="border border-slate-200 rounded-sm p-5 bg-slate-50/50" data-testid={`partner-brand-${idx}`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-display text-lg text-[#0A1128]">Brand #{idx + 1}</h3>
                  <button type="button" onClick={() => removeBrand(idx)} data-testid={`remove-brand-${idx}`} className="text-slate-400 hover:text-red-500 p-1.5">
                    <Trash2 size={14} />
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 block mb-2">Brand Name *</label>
                    <input
                      value={b.name || ""}
                      onChange={(e) => setBrand(idx, "name", e.target.value)}
                      placeholder="NakiaQurbani"
                      data-testid={`brand-name-${idx}`}
                      className="w-full px-4 py-3 border border-slate-300 rounded-sm text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 block mb-2">Website Link *</label>
                    <input
                      value={b.link || ""}
                      onChange={(e) => setBrand(idx, "link", e.target.value)}
                      placeholder="https://nakiaqurbani.com"
                      data-testid={`brand-link-${idx}`}
                      className="w-full px-4 py-3 border border-slate-300 rounded-sm text-sm font-mono"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <FileUploader
                    label="Brand Preview Image (website screenshot or banner)"
                    value={b.image_url}
                    onChange={(v) => setBrand(idx, "image_url", v)}
                    testId={`brand-image-${idx}`}
                  />
                </div>
                <div className="space-y-4">
                  <MultilingualField
                    label="Tagline"
                    value={b.tagline}
                    onChange={(v) => setBrand(idx, "tagline", v)}
                    testId={`brand-tagline-${idx}`}
                  />
                  <MultilingualField
                    label="Description"
                    value={b.description}
                    onChange={(v) => setBrand(idx, "description", v)}
                    testId={`brand-desc-${idx}`}
                    multiline
                  />
                </div>
                <div className="mt-4 max-w-[160px]">
                  <label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 block mb-2">Sort Order</label>
                  <input
                    type="number"
                    value={b.sort_order ?? 0}
                    onChange={(e) => setBrand(idx, "sort_order", +e.target.value)}
                    data-testid={`brand-sort-${idx}`}
                    className="w-full px-4 py-3 border border-slate-300 rounded-sm text-sm"
                  />
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={addBrand}
              data-testid="add-partner-brand"
              className="border border-dashed border-slate-300 hover:border-[#D4AF37] hover:text-[#D4AF37] text-slate-500 text-xs uppercase tracking-[0.2em] px-5 py-3 rounded-sm flex items-center gap-2"
            >
              <Plus size={14} /> Add Partner Brand
            </button>
          </div>
        </Card>

        {/* SEO */}
        <Card title="SEO Defaults">
          <MultilingualField label="Default Site Title" value={data.seo.title} onChange={(v) => setData({ ...data, seo: { ...data.seo, title: v } })} testId="seo-title" />
        </Card>
      </div>
    </div>
  );
};

const Card = ({ title, children }) => (
  <section className="bg-white border border-slate-200 rounded-sm p-6 md:p-8">
    <h2 className="font-display text-xl text-[#0A1128] mb-6">{title}</h2>
    {children}
  </section>
);

const Input = ({ label, v, on, testId }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 block mb-2">{label}</label>
    <input value={v} onChange={(e) => on(e.target.value)} data-testid={testId} className="w-full px-4 py-3 border border-slate-300 rounded-sm text-sm" />
  </div>
);

export default AdminSettings;
